import {
  globalHumanReadableRender,
  IAttributeOperator,
  IBetweenValues,
  wrapWithProtectionCondition,
  wrapWithProtectionConditionForNegation
} from './crm-entities-base-operators';
import { TranslateService } from '@ngx-translate/core';

import * as moment_ from 'moment';
const moment  = moment_;

class DateTimeOperatorBase {
  freeMarkerOperator: string;
  name: string;
  displayNameKey: string;
  humanReadableTranslationKey: string;

  renderHumanReadable(translate: TranslateService, left: string, right: any) {
    //LLL will show as 'September 4, 1986 8:30 PM'
    let formattedRightHandSide = moment(right).format('LLL');
    return globalHumanReadableRender.apply(this, [translate, left, formattedRightHandSide]);
  }

  renderFreeMarker(left: string, right: any) {
    return `${wrapWithProtectionCondition(left)} && (${left}?datetime.iso ${this.freeMarkerOperator} "${right}"?datetime.iso)`;
  }

  //subclasses reusable functions:
  renderHumanReadableDateOnly(translate: TranslateService, left: string, right: any) {
    //LL will show as 'September 4, 1986'
    let formattedRightHandSide = moment(right).format('LL');
    return globalHumanReadableRender.apply(this, [translate, left, formattedRightHandSide]);
  }

  //this function will be used only on the DateOnly concrete classes
  renderFreeMarkerDateOnly(left: string, right: string) {
    return `(${wrapWithProtectionCondition(left)} && (${left}?datetime.iso?date ${this.freeMarkerOperator} "${right}"?datetime.iso?date))`;
  }
}

export class DateTimeOperatorEquals extends DateTimeOperatorBase implements IAttributeOperator {
  name = 'equalsDateTime';
  displayNameKey = 'COMMON.CRM_ENTITY_OPERATORS.EQUALS';
  humanReadableTranslationKey = 'COMMON.CRM_ENTITY_OPERATORS_HUMAN_READABLE.EQUALS';
  freeMarkerOperator = '==';
}

export class DateTimeOperatorDifferent extends DateTimeOperatorBase {
  name = 'differentDateTime';
  displayNameKey = 'COMMON.CRM_ENTITY_OPERATORS.DIFFERENT';
  humanReadableTranslationKey = 'COMMON.CRM_ENTITY_OPERATORS_HUMAN_READABLE.DIFFERENT';
  freeMarkerOperator = '!=';

  renderFreeMarker(left: string, right: any) {
    return `${wrapWithProtectionConditionForNegation(left)} || (${left}?datetime.iso ${this.freeMarkerOperator} "${right}"?datetime.iso)`;
  }
}

export class DateTimeOperatorLessThan extends DateTimeOperatorBase {
  name = 'ltDateTime';
  displayNameKey = 'COMMON.CRM_ENTITY_OPERATORS.LESS_THAN';
  humanReadableTranslationKey = 'COMMON.CRM_ENTITY_OPERATORS_HUMAN_READABLE.LESS_THAN';
  freeMarkerOperator = '<';
}

export class DateTimeOperatorLessThanEqual extends DateTimeOperatorBase {
  name = 'lteDateTime';
  displayNameKey = 'COMMON.CRM_ENTITY_OPERATORS.LESS_OR_EQUAL_TO';
  humanReadableTranslationKey = 'COMMON.CRM_ENTITY_OPERATORS_HUMAN_READABLE.LESS_OR_EQUAL_TO';
  freeMarkerOperator = '<=';
}

export class DateTimeOperatorGreaterThan extends DateTimeOperatorBase {
  name = 'gtDateTime';
  displayNameKey = 'COMMON.CRM_ENTITY_OPERATORS.GREATER_THAN';
  humanReadableTranslationKey = 'COMMON.CRM_ENTITY_OPERATORS_HUMAN_READABLE.GREATER_THAN';
  freeMarkerOperator = '>';
}

export class DateTimeOperatorGreaterOrEqualTo extends DateTimeOperatorBase {
  name = 'gteDateTime';
  displayNameKey = 'COMMON.CRM_ENTITY_OPERATORS.GREATER_OR_EQUAL_TO';
  humanReadableTranslationKey = 'COMMON.CRM_ENTITY_OPERATORS_HUMAN_READABLE.GREATER_OR_EQUAL_TO';
  freeMarkerOperator = '>=';
}

export class DateTimeOperatorBetween extends DateTimeOperatorBase {
  name = 'betweenDateTime';
  displayNameKey = 'COMMON.CRM_ENTITY_OPERATORS.BETWEEN';
  humanReadableTranslationKey = 'COMMON.CRM_ENTITY_OPERATORS_HUMAN_READABLE.IS_BETWEEN';

  renderFreeMarker(left: string, right: IBetweenValues) {
    return `${wrapWithProtectionCondition(left)} && ( 
      (${left}?datetime.iso >= "${right.gteValue}"?datetime.iso) && 
      (${left}?datetime.iso <= "${right.lteValue}"?datetime.iso))`;
  }

  renderHumanReadable(translate: TranslateService, left: string, right: IBetweenValues) {
    let formattedRightHandSideLte = moment(right.lteValue).format('LLL');
    let formattedRightHandSideGte = moment(right.gteValue).format('LLL');
    return translate.instant(this.humanReadableTranslationKey, {
      leftValue: left,
      gteValue: formattedRightHandSideGte,
      lteValue: formattedRightHandSideLte });
  }
}

///////////////////////////
//  Date-Only operators  //
///////////////////////////
export class DateOnlyOperatorEquals extends DateTimeOperatorEquals {
  name = 'equalsDateOnly';

  renderFreeMarker(left: string, right: string) {
    return this.renderFreeMarkerDateOnly(left, right);
  }

  renderHumanReadable(translate: TranslateService, left: string, right: IBetweenValues) {
    return this.renderHumanReadableDateOnly(translate, left, right);
  }
}

export class DateOnlyOperatorDifferent extends DateTimeOperatorDifferent {
  name = 'differentDateOnly';

  renderFreeMarker(left: string, right: string) {
    return `(${wrapWithProtectionConditionForNegation(left)} || (${left}?datetime.iso?date ${this.freeMarkerOperator} "${right}"?datetime.iso?date))`;
  }

  renderHumanReadable(translate: TranslateService, left: string, right: IBetweenValues) {
    return this.renderHumanReadableDateOnly(translate, left, right);
  }
}

export class DateOnlyOperatorGreaterThan extends DateTimeOperatorGreaterThan {
  name = 'gtDateOnly';

  renderFreeMarker(left: string, right: string) {
    return this.renderFreeMarkerDateOnly(left, right);
  }

  renderHumanReadable(translate: TranslateService, left: string, right: IBetweenValues) {
    return this.renderHumanReadableDateOnly(translate, left, right);
  }
}

export class DateOnlyOperatorGreaterOrEqualTo extends DateTimeOperatorGreaterOrEqualTo {
  name = 'gteDateOnly';

  renderFreeMarker(left: string, right: string) {
    return this.renderFreeMarkerDateOnly(left, right);
  }

  renderHumanReadable(translate: TranslateService, left: string, right: IBetweenValues) {
    return this.renderHumanReadableDateOnly(translate, left, right);
  }
}

export class DateOnlyOperatorLessThan extends DateTimeOperatorLessThan {
  name = 'ltDateOnly';

  renderFreeMarker(left: string, right: string) {
    return this.renderFreeMarkerDateOnly(left, right);
  }

  renderHumanReadable(translate: TranslateService, left: string, right: IBetweenValues) {
    return this.renderHumanReadableDateOnly(translate, left, right);
  }
}

export class DateOnlyOperatorLessThanEqual extends DateTimeOperatorLessThanEqual {
  name = 'lteDateOnly';

  renderFreeMarker(left: string, right: string) {
    return this.renderFreeMarkerDateOnly(left, right);
  }

  renderHumanReadable(translate: TranslateService, left: string, right: IBetweenValues) {
    return this.renderHumanReadableDateOnly(translate, left, right);
  }
}

export class DateOnlyOperatorBetween extends DateTimeOperatorBetween {
  name = 'betweenDateOnly';

  renderFreeMarker(left: string, right: IBetweenValues) {
    return `${wrapWithProtectionCondition(left)} && ( 
      (${left}?datetime.iso?date >= "${right.gteValue}"?datetime.iso?date) && 
      (${left}?datetime.iso?date <= "${right.lteValue}"?datetime.iso?date))`;
  }

  renderHumanReadable(translate: TranslateService, left: string, right: IBetweenValues) {
    let formattedRightHandSideLte = moment(right.lteValue).format('LL');
    let formattedRightHandSideGte = moment(right.gteValue).format('LL');
    return translate.instant(this.humanReadableTranslationKey, {
      leftValue: left,
      gteValue: formattedRightHandSideGte,
      lteValue: formattedRightHandSideLte });
  }
}
